<!-- Summary stats cards -->
<div fxLayout="row" fxLayout.lt-sm="column" fxLayoutGap="12px" class="m-b-20">
  <mat-card class="stat-card" fxFlex>
    <mat-card-content>
      <div class="stat-label text-primary">TEMPLATES</div>
      <div class="stat-value">{{ totalTemplates }}</div>
    </mat-card-content>
  </mat-card>
  <mat-card class="stat-card" fxFlex>
    <mat-card-content>
      <div class="stat-label text-accent">AFFECTED HOMES</div>
      <div class="stat-value">{{ totalAffectedHomes }}</div>
    </mat-card-content>
  </mat-card>
  <mat-card class="stat-card" fxFlex>
    <mat-card-content>
      <div class="stat-label" style="color: #8b5cf6;">SOURCE HOMES</div>
      <div class="stat-value">{{ totalSourceHomes }}</div>
    </mat-card-content>
  </mat-card>
  <mat-card class="stat-card" fxFlex>
    <mat-card-content>
      <div class="stat-label text-warn">TOTAL IFC FILES</div>
      <div class="stat-value">{{ totalIfcFiles }}</div>
    </mat-card-content>
  </mat-card>
</div>

<!-- Search and actions bar -->
<mat-card [ngClass.lt-sm]="{'mat-card-inline-sm': true}">
  <mat-card-content fxLayout="column" fxLayoutAlign="start center">
    <div fxFlex class="filter w-100" fxLayoutAlign="center center"
         fxLayout="row" fxLayoutGap="15px" fxLayout.lt-sm="column">
      <mat-form-field class="accent">
        <mat-label>Search {{ templates?.length ?? 0 }} templates</mat-label>
        <input matInput type="text"
          [(ngModel)]="searchTerm"
          (keyup.enter)="loadData()"
          (keyup.esc)="searchTerm=''; loadData()"
          name="searchTerm"
          placeholder="Search templates, homes, render codes...">
        <button mat-button *ngIf="searchTerm" matSuffix mat-icon-button
                aria-label="Clear"
                (click)="searchTerm=''; loadData()">
          <mat-icon>close</mat-icon>
        </button>
      </mat-form-field>
      <div fxLayout="row" fxLayoutGap="10px">
        <button type="submit" mat-flat-button color="primary"
                (click)="loadData()"
                matTooltip="Search"
                [class.button-spinner]="loading"
                [disabled]="loading">
          <mat-icon>search</mat-icon> Search
        </button>
        <button mat-stroked-button
                (click)="syncAllTemplates()"
                matTooltip="Sync every template in this company"
                [class.button-spinner]="syncingAll"
                [disabled]="loading || syncingAll || templates.length === 0">
          <mat-icon>sync</mat-icon> Sync All
        </button>
        <button mat-flat-button color="accent" (click)="openAddDialog()">
          <mat-icon>add</mat-icon> New Template
        </button>
      </div>
    </div>
  </mat-card-content>
</mat-card>

<!-- Loading bar -->
<mat-progress-bar *ngIf="loading" mode="indeterminate" class="m-b-10">
</mat-progress-bar>

<!-- Template list -->
<mat-card class="contrast template-card"
          *ngFor="let template of templates"
          [class.expanded]="expandedTemplateId === template.id"
          [ngClass.lt-sm]="{'mat-card-inline-sm': true}">
  <mat-card-content fxLayout="column">
    <!-- Header row (clickable to expand) -->
    <div class="template-header"
         (click)="toggleExpand(template)"
         fxLayout="row" fxLayoutAlign="space-between start"
         fxLayout.lt-sm="column">
      <div fxLayout="column" fxFlex>
        <!-- Name, render code, status -->
        <div fxLayout="row" fxLayoutAlign="start center"
             fxLayoutGap="10px" class="m-b-5">
          <strong class="template-name">{{ template.name }}</strong>
          <span class="render-code-chip">{{ template.renderCode }}</span>
          <mat-chip-list>
            <mat-chip class="status-chip"
                      [ngClass]="getStatusClass(template.status)">
              <mat-icon class="status-icon">
                {{ getStatusIcon(template.status) }}
              </mat-icon>
              {{ getStatusLabel(template.status) }}
            </mat-chip>
          </mat-chip-list>
        </div>
        <!-- Description -->
        <div *ngIf="template.description" class="template-description">
          {{ template.description }}
        </div>
        <!-- Affected homes chips + counts -->
        <div fxLayout="row wrap" fxLayoutAlign="start center"
             fxLayoutGap="8px" class="m-t-10">
          <span class="detail-label m-r-5" style="margin-bottom: 0;">
            Affected homes:
          </span>
          <mat-chip-list>
            <mat-chip *ngFor="let home of template.affectedHomes"
                      class="affected-home-chip"
                      [ngClass]="{
                        'home-existing': home.isExisting,
                        'home-new': !home.isExisting
                      }">
              {{ home.homeName }}
              <span class="builder-label"
                    *ngIf="home.builderName">
                ({{ home.builderName }})
              </span>
              <span *ngIf="home.isExisting"
                    class="home-tag existing-tag">EXISTING</span>
              <span *ngIf="!home.isExisting"
                    class="home-tag new-tag">NEW</span>
            </mat-chip>
          </mat-chip-list>
          <span class="count-summary">
            &bull; {{ template.sourceCount ?? 0 }}
            source{{ (template.sourceCount ?? 0) !== 1 ? 's' : '' }}
            &bull; {{ template.totalIfcFiles ?? 0 }}
            IFC file{{ (template.totalIfcFiles ?? 0) !== 1 ? 's' : '' }}
          </span>
        </div>
      </div>

      <!-- Actions -->
      <div fxLayout="row" fxLayoutAlign="end center" fxLayoutGap="8px"
           (click)="$event.stopPropagation()">
        <button mat-stroked-button class="action-btn"
                [color]="template.status === placeholderStatus.SourceUpdated ? 'warn' : ''"
                [disabled]="syncingTemplateId === template.id"
                (click)="syncTemplate(template, $event)"
                matTooltip="Copy IFC files from sources to affected homes">
          <mat-icon>sync</mat-icon> Sync
        </button>
        <button mat-stroked-button class="action-btn"
                (click)="openEditDialog(template, $event)"
                matTooltip="Edit template">
          <mat-icon>edit</mat-icon> Edit
        </button>
        <button mat-icon-button
                (click)="deleteTemplate(template, $event)"
                matTooltip="Delete template">
          <mat-icon>delete_outline</mat-icon>
        </button>
        <mat-icon class="expand-icon"
                  [class.rotated]="expandedTemplateId === template.id">
          expand_more
        </mat-icon>
      </div>
    </div>

    <!-- Expanded detail section -->
    <div *ngIf="expandedTemplateId === template.id"
         class="expanded-detail">
      <mat-progress-bar *ngIf="loadingDetail" mode="indeterminate"
                        class="m-b-10">
      </mat-progress-bar>

      <ng-container *ngIf="expandedDetail">
        <!-- Source home mappings header -->
        <div fxLayout="row" fxLayoutAlign="start center"
             fxLayoutGap="8px" class="m-b-10">
          <span class="source-badge">S</span>
          <strong>Source Home Mappings</strong>
        </div>

        <!-- Source mapping cards -->
        <mat-card *ngFor="let mapping of expandedDetail.sourceMappings"
                  class="detail-card m-b-10">
          <mat-card-content>
            <div fxLayout="row" fxLayoutAlign="space-between center"
                 class="m-b-10">
              <div fxLayout="row" fxLayoutAlign="start center"
                   fxLayoutGap="8px">
                <strong>{{ mapping.sourceHomeName }}</strong>
                <span class="builder-label"
                      *ngIf="mapping.builderName">
                  via {{ mapping.builderName }}
                </span>
              </div>
              <small class="text-muted">
                Synced: {{ formatDate(mapping.lastSyncedDate) }}
              </small>
            </div>
            <div fxLayout="row wrap" fxLayoutGap="6px">
              <mat-chip-list>
                <mat-chip *ngFor="let file of mapping.sourceFiles"
                          class="file-chip"
                          [ngClass]="{
                            'master-file-chip': file.type === 'Master',
                            'option-file-chip': file.type === 'Option'
                          }">
                  {{ file.name }}
                </mat-chip>
              </mat-chip-list>
            </div>
          </mat-card-content>
        </mat-card>

        <!-- Complete coverage summary -->
        <mat-card class="coverage-card">
          <mat-card-content>
            <div class="detail-label m-b-10">
              COMPLETE FILE COVERAGE
            </div>
            <div fxLayout="row wrap" fxLayoutGap="6px">
              <mat-chip-list>
                <mat-chip *ngFor="let file of expandedFiles"
                          class="file-chip"
                          [ngClass]="{
                            'master-file-chip': file.isMaster,
                            'option-file-chip': !file.isMaster
                          }">
                  <span class="file-name">{{ file.name }}</span>
                  <span class="file-source">
                    &larr; {{ file.source }}
                  </span>
                </mat-chip>
              </mat-chip-list>
            </div>
          </mat-card-content>
        </mat-card>

        <!-- Per-home file status breakdown -->
        <mat-card *ngIf="hasAnyFileStatuses()"
                  class="coverage-card m-t-10">
          <mat-card-content>
            <div class="detail-label m-b-10">
              FILE STATUS PER HOME
            </div>
            <div *ngFor="let home of expandedDetail.affectedHomes"
                 class="m-b-15">
              <div fxLayout="row" fxLayoutAlign="start center"
                   fxLayoutGap="8px" class="m-b-5">
                <strong>{{ home.homeName }}</strong>
              </div>
              <div fxLayout="row wrap" fxLayoutGap="6px"
                   *ngIf="home.fileStatuses?.length > 0">
                <mat-chip-list>
                  <mat-chip *ngFor="let fs of home.fileStatuses"
                            class="file-chip"
                            [ngClass]="{
                              'file-status-original': fs.status === 'original',
                              'file-status-placeholder': fs.status === 'placeholder',
                              'file-status-overridden': fs.status === 'overridden',
                              'file-status-missing': fs.status === 'missing',
                              'file-chip-actionable':
                                (fs.status === 'original' || fs.status === 'overridden')
                                && overridingFileId !== fs.homeIfcFileId
                            }"
                            [matTooltip]="getFileStatusTooltip(fs)"
                            [matMenuTriggerFor]="
                              (fs.status === 'original' || fs.status === 'overridden')
                                ? fileMenu : null"
                            [matMenuTriggerData]="{ fs: fs }"
                            [disabled]="overridingFileId === fs.homeIfcFileId">
                    <mat-icon class="file-status-icon">
                      {{ getFileStatusIcon(fs.status) }}
                    </mat-icon>
                    {{ fs.fileName }}
                  </mat-chip>
                </mat-chip-list>

                <!-- PORTAL-2203: override actions, shared across the file chips above -->
                <mat-menu #fileMenu="matMenu">
                  <ng-template matMenuContent let-fs="fs">
                    <button mat-menu-item *ngIf="fs.status === 'original'"
                            (click)="setFileOverride(fs, true)">
                      <mat-icon>published_with_changes</mat-icon>
                      <span>Override with placeholder</span>
                    </button>
                    <button mat-menu-item *ngIf="fs.status === 'overridden'"
                            (click)="setFileOverride(fs, false)">
                      <mat-icon>undo</mat-icon>
                      <span>Remove override</span>
                    </button>
                  </ng-template>
                </mat-menu>
              </div>
            </div>
            <div fxLayout="row" fxLayoutGap="12px" class="m-t-10">
              <div fxLayout="row" fxLayoutAlign="start center"
                   fxLayoutGap="4px">
                <mat-icon class="file-status-icon"
                          style="color: #16a34a;">
                  insert_drive_file
                </mat-icon>
                <small class="text-muted">Original</small>
              </div>
              <div fxLayout="row" fxLayoutAlign="start center"
                   fxLayoutGap="4px">
                <mat-icon class="file-status-icon"
                          style="color: #6366f1;">
                  content_copy
                </mat-icon>
                <small class="text-muted">Placeholder</small>
              </div>
              <div fxLayout="row" fxLayoutAlign="start center"
                   fxLayoutGap="4px">
                <mat-icon class="file-status-icon"
                          style="color: #d97706;">
                  published_with_changes
                </mat-icon>
                <small class="text-muted">Overridden</small>
              </div>
              <div fxLayout="row" fxLayoutAlign="start center"
                   fxLayoutGap="4px">
                <mat-icon class="file-status-icon"
                          style="color: #94a3b8;">
                  remove_circle_outline
                </mat-icon>
                <small class="text-muted">
                  Not synced
                </small>
              </div>
            </div>
          </mat-card-content>
        </mat-card>

        <div class="expanded-footer">
          <mat-icon class="detail-icon text-muted">info</mat-icon>
          <small class="text-muted">
            Facade renders only &mdash; panoramas are not available
            for placeholder templates.
            Created {{ formatDate(expandedDetail.createdDate) }}.
          </small>
        </div>
      </ng-container>
    </div>
  </mat-card-content>
</mat-card>

<!-- Empty state -->
<mat-card *ngIf="!loading && templates.length === 0" class="contrast">
  <mat-card-content fxLayoutAlign="center center"
                    style="padding: 48px;">
    <span class="text-muted">
      No placeholder templates found.
    </span>
  </mat-card-content>
</mat-card>
